import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUserPlus, FiEye } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, today, unwrapList } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyForm = {
  name: '', head_type: '24', threshold_stitches: 400000, rate_per_1000: 280,
  fixed_below_threshold: 1400, special_thread_price: 200, is_active: true,
};

export default function Machines() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [assignModal, setAssignModal] = useState(false);
  const [activeMachine, setActiveMachine] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignForm, setAssignForm] = useState({ employee_id: '', shift_id: '', assigned_date: today() });
  const [shifts, setShifts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.machines.list(params);
      const list = Array.isArray(data.data) ? data.data : data.machines || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load machines', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.employees.list({ per_page: 500 }).then(({ data }) => {
      setEmployees(unwrapList(data, 'employees'));
    }).catch(() => {});
    api.shifts.list({ per_page: 500 }).then(({ data }) => {
      const raw = data.shifts || data.data || data || [];
      setShifts(Array.isArray(raw) ? raw : []);
    }).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setErrors({}); setModal(true); };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name, head_type: row.head_type, threshold_stitches: row.threshold_stitches,
      rate_per_1000: row.rate_per_1000, fixed_below_threshold: row.fixed_below_threshold,
      special_thread_price: row.special_thread_price, is_active: row.is_active !== false,
    });
    setErrors({});
    setModal(true);
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (!form.head_type) errs.head_type = 'Head type is required';
    if (!form.threshold_stitches) errs.threshold_stitches = 'Threshold is required';
    if (!form.rate_per_1000) errs.rate_per_1000 = 'Rate is required';
    if (!form.fixed_below_threshold) errs.fixed_below_threshold = 'Fixed below threshold is required';
    if (!form.special_thread_price) errs.special_thread_price = 'Special thread price is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.machines.update(editing.id, form);
        toast('success', '', 'Machine updated');
      } else {
        await api.machines.create(form);
        toast('success', '', 'Machine created with default rates');
      }
      setModal(false);
      load();
    } catch (err) {
      showError('Failed to save machine', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`machine "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.machines.remove(row.id);
      toast('success', '', 'Machine deleted');
      load();
    } catch (err) {
      showError('Failed to delete machine', extractError(err));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.machines.update(row.id, { is_active: !row.is_active });
      toast('success', '', `Machine ${row.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch (err) {
      showError('Failed to toggle status', extractError(err));
    }
  };

  const openAssign = (row) => {
    setActiveMachine(row);
    setAssignForm({ employee_id: '', shift_id: '', assigned_date: today() });
    setErrors({});
    setAssignModal(true);
  };

  const handleAssign = async () => {
    const errs = {};
    if (!assignForm.employee_id) errs.employee_id = 'Employee is required';
    if (!assignForm.assigned_date) errs.assigned_date = 'Assigned date is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await api.machines.assign(activeMachine.id, assignForm);
      toast('success', '', 'Employee assigned');
      setAssignModal(false);
      load();
    } catch (err) {
      showError('Failed to assign', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Machines"
        subtitle="24-head and 30-head embroidery machines with rate settings."
        crumbs={[{ label: 'Production' }, { label: 'Machines' }]}
        actions={hasPermission('create-machines') && (
          <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Add Machine</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="machines" label="machines" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No machines found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Machine', render: (r) => <Link to={`/machines/${r.id}`} style={{ fontWeight: 600 }}>{r.name}</Link> },
              { key: 'head_type', label: 'Heads', render: (r) => <span className="badge badge-blue">{r.head_type} Head</span> },
              { key: 'rate_per_1000', label: 'Rate/1000', align: 'right', render: (r) => formatMoney(r.rate_per_1000, 0) },
              { key: 'fixed_below_threshold', label: 'Fixed Below', align: 'right', render: (r) => formatMoney(r.fixed_below_threshold, 0) },
              { key: 'special_thread_price', label: 'Special Thread', align: 'right', render: (r) => formatMoney(r.special_thread_price, 0) },
              { key: 'is_active', label: 'Status', render: (r) => (
                <button
                  className={`toggle-btn ${r.is_active ? 'active' : ''}`}
                  onClick={() => toggleActive(r)}
                  title={r.is_active ? 'Click to deactivate' : 'Click to activate'}
                  type="button"
                >
                  <span className="toggle-knob" />
                </button>
              ) },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <Link to={`/machines/${r.id}`} className="btn-icon"><FiEye /></Link>
                  {hasPermission('manage-assignments') && <button className="btn-icon" title="Assign" onClick={() => openAssign(r)}><FiUserPlus /></button>}
                  {hasPermission('update-machines') && <button className="btn-icon" onClick={() => openEdit(r)}><FiEdit2 /></button>}
                  {hasPermission('delete-machines') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={modal} onClose={() => { setErrors({}); setModal(false); }} title={editing ? 'Edit Machine' : 'Add Machine'} size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </>}
      >
        <div className="grid-3">
          <div className="form-group"><label className="form-label">Name <span className="req">*</span></label>
            <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}</div>
          <div className="form-group"><label className="form-label">Head Type <span className="req">*</span></label>
            <SearchableSelect options={[{ value: '24', label: '24 Head' }, { value: '30', label: '30 Head' }]} value={form.head_type} onChange={(v) => {
              const type = v;
              setForm({
                ...form, head_type: type,
                rate_per_1000: type === '24' ? 280 : 300,
                fixed_below_threshold: type === '24' ? 1400 : 1500,
              });
              if (errors.head_type) setErrors((p) => ({ ...p, head_type: '' }));
            }} isInvalid={!!errors.head_type} placeholder="-- Select --" />
            {errors.head_type && <div className="invalid-feedback">{errors.head_type}</div>}</div>
          <div className="form-group"><label className="form-label">Threshold (stitches) <span className="req">*</span></label>
            <input className={`form-control ${errors.threshold_stitches ? 'is-invalid' : ''}`} type="number" value={form.threshold_stitches} onChange={(e) => { setForm({ ...form, threshold_stitches: e.target.value }); if (errors.threshold_stitches) setErrors((p) => ({ ...p, threshold_stitches: '' })); }} />
            {errors.threshold_stitches && <div className="invalid-feedback">{errors.threshold_stitches}</div>}</div>
          <div className="form-group"><label className="form-label">Rate / 1000 stitches <span className="req">*</span></label>
            <input className={`form-control ${errors.rate_per_1000 ? 'is-invalid' : ''}`} type="number" value={form.rate_per_1000} onChange={(e) => { setForm({ ...form, rate_per_1000: e.target.value }); if (errors.rate_per_1000) setErrors((p) => ({ ...p, rate_per_1000: '' })); }} />
            {errors.rate_per_1000 && <div className="invalid-feedback">{errors.rate_per_1000}</div>}</div>
          <div className="form-group"><label className="form-label">Fixed below threshold <span className="req">*</span></label>
            <input className={`form-control ${errors.fixed_below_threshold ? 'is-invalid' : ''}`} type="number" value={form.fixed_below_threshold} onChange={(e) => { setForm({ ...form, fixed_below_threshold: e.target.value }); if (errors.fixed_below_threshold) setErrors((p) => ({ ...p, fixed_below_threshold: '' })); }} />
            {errors.fixed_below_threshold && <div className="invalid-feedback">{errors.fixed_below_threshold}</div>}</div>
          <div className="form-group"><label className="form-label">Special thread price <span className="req">*</span></label>
            <input className={`form-control ${errors.special_thread_price ? 'is-invalid' : ''}`} type="number" value={form.special_thread_price} onChange={(e) => { setForm({ ...form, special_thread_price: e.target.value }); if (errors.special_thread_price) setErrors((p) => ({ ...p, special_thread_price: '' })); }} />
            {errors.special_thread_price && <div className="invalid-feedback">{errors.special_thread_price}</div>}</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active machine
        </label>
      </Modal>

      <Modal open={assignModal} onClose={() => { setErrors({}); setAssignModal(false); }} title={`Assign Employee — ${activeMachine?.name || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAssignModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAssign}>Assign</button>
        </>}
      >
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Employee <span className="req">*</span></label>
            <SearchableSelect options={employees} value={assignForm.employee_id} onChange={(v) => { setAssignForm({ ...assignForm, employee_id: v }); if (errors.employee_id) setErrors((p) => ({ ...p, employee_id: '' })); }} isInvalid={!!errors.employee_id} placeholder="-- Select operator --" />
            {errors.employee_id && <div className="invalid-feedback">{errors.employee_id}</div>}</div>
          <div className="form-group"><label className="form-label">Shift</label>
            <SearchableSelect
              options={[{ value: '', label: '— None —' }, ...shifts.filter((s) => s.is_active !== false).map((s) => ({ value: s.id, label: s.name }))]}
              value={assignForm.shift_id} onChange={(v) => setAssignForm({ ...assignForm, shift_id: v })} placeholder="-- Optional --"
            /></div>
          <div className="form-group"><label className="form-label">Assigned Date <span className="req">*</span></label>
            <input className={`form-control ${errors.assigned_date ? 'is-invalid' : ''}`} type="date" value={assignForm.assigned_date} onChange={(e) => { setAssignForm({ ...assignForm, assigned_date: e.target.value }); if (errors.assigned_date) setErrors((p) => ({ ...p, assigned_date: '' })); }} />
            {errors.assigned_date && <div className="invalid-feedback">{errors.assigned_date}</div>}</div>
        </div>
        {(activeMachine?.assignments || []).some((a) => !a.released_date) && (
          <div className="alert-banner warn">
            <b>Currently assigned:</b>
            <div style={{ marginTop: 6 }}>
              {(activeMachine?.assignments || []).filter((a) => !a.released_date).map((a, i) => (
                <span key={i} className="badge badge-green" style={{ marginRight: 6 }}>
                  {a.employee?.name || '—'}{a.shift ? ` (${a.shift.name})` : ''}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 4, fontSize: 12 }}>You can assign more (multi-assignment allowed).</div>
          </div>
        )}
      </Modal>
    </div>
  );
}